import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import type { DiagramScene, SceneRegion, SceneGroup, SceneIndicator, SceneLocationGroup } from '../api/types'
import { useTheme } from '../contexts/ThemeContext'
import { NodeCard } from './NodeCard'
import { EdgeShape, EdgeArrowHead, EdgeLabel, EdgeMarker, EdgeParticles, resolveAllLabelPositions } from './EdgeShape'
import type { StructureFilters } from './CanvasSettings'
import styles from './SceneRenderer.module.css'

export interface Transform {
    x: number
    y: number
    scale: number
}

/** Thresholds for progressive performance degradation */
const PERF = {
    /** Above this element count, disable SVG filters & particles */
    LARGE_GRAPH: 80,
    /** Above this, also disable edge labels and hover transitions */
    HUGE_GRAPH: 200,
} as const

interface ViewportBounds {
    left: number; top: number; right: number; bottom: number
}

interface SceneRendererProps {
    scene: DiagramScene
    selectedNodeId?: string | null
    selectedIds?: Set<string>
    onNodeClick?: (nodeId: string, shiftKey: boolean) => void
    onNodeDoubleClick?: (nodeId: string, newName?: string, oldName?: string) => void
    onNodeHover?: (nodeId: string | null) => void
    transform: Transform
    onTransformChange: React.Dispatch<React.SetStateAction<Transform>>
    svgRef?: React.RefObject<SVGSVGElement | null>
    isSimulating?: boolean
    simSpeed?: number
    structureFilters?: StructureFilters
}

export function SceneRenderer({
    scene, selectedNodeId, selectedIds,
    onNodeClick, onNodeDoubleClick, onNodeHover,
    transform, onTransformChange,
    svgRef: externalSvgRef,
    isSimulating, simSpeed = 1,
    structureFilters,
}: SceneRendererProps) {
    const internalSvgRef = useRef<SVGSVGElement | null>(null)
    const svgRef = externalSvgRef || internalSvgRef
    const setTransform = onTransformChange
    const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)
    const panRef = useRef<{ active: boolean; pointerId: number; x: number; y: number } | null>(null)
    const pinchRef = useRef<{ pointerId1: number; pointerId2: number; dist: number; midX: number; midY: number } | null>(null)
    const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())
    const rafRef = useRef<number | null>(null)
    const pendingTransformRef = useRef<Transform | null>(null)
    const [svgSize, setSvgSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })

    const { theme } = useTheme()

    // Track SVG element size for viewport culling
    useEffect(() => {
        const svg = svgRef.current
        if (!svg) return
        const ro = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect
            setSvgSize({ w: width, h: height })
        })
        ro.observe(svg)
        return () => ro.disconnect()
    }, [])

    // Actors are embedded into action nodes (compound rendering).
    // Filter them out as a safety net in case the server hasn't been updated yet.
    const renderNodes = useMemo(() =>
        scene.nodes.filter(n => n.semanticRole.toUpperCase() !== 'ACTOR'),
        [scene.nodes]
    )
    const renderEdges = useMemo(() =>
        scene.edges.filter(e => e.category.toUpperCase() !== 'AGENCY'),
        [scene.edges]
    )

    // Performance tier based on element count
    const elementCount = renderNodes.length + renderEdges.length
    const isLargeGraph = elementCount > PERF.LARGE_GRAPH
    const isHugeGraph = elementCount > PERF.HUGE_GRAPH

    // Viewport culling — compute visible bounds in scene coordinates
    const viewport = useMemo<ViewportBounds>(() => {
        if (svgSize.w === 0 || svgSize.h === 0) {
            // No size yet — show everything
            return { left: -Infinity, top: -Infinity, right: Infinity, bottom: Infinity }
        }
        const s = transform.scale
        return {
            left: -transform.x / s,
            top: -transform.y / s,
            right: (svgSize.w - transform.x) / s,
            bottom: (svgSize.h - transform.y) / s,
        }
    }, [transform.x, transform.y, transform.scale, svgSize.w, svgSize.h])

    // Margin around viewport for culling (nodes partially outside viewport should still render)
    const cullMargin = 100

    const visibleNodes = useMemo(() => {
        if (!isLargeGraph) return renderNodes // small graph: render all
        return renderNodes.filter(n => {
            const left = n.x - n.width / 2
            const top = n.y - n.height / 2
            return (
                left + n.width > viewport.left - cullMargin &&
                left < viewport.right + cullMargin &&
                top + n.height > viewport.top - cullMargin &&
                top < viewport.bottom + cullMargin
            )
        })
    }, [renderNodes, viewport, isLargeGraph])

    const visibleEdges = useMemo(() => {
        if (!isLargeGraph) return renderEdges
        // For edges, check if any point falls inside the extended viewport
        return renderEdges.filter(e =>
            e.points.some(p =>
                p.x > viewport.left - cullMargin && p.x < viewport.right + cullMargin &&
                p.y > viewport.top - cullMargin && p.y < viewport.bottom + cullMargin
            )
        )
    }, [renderEdges, viewport, isLargeGraph])

    // Layer-level edge label collision pass (mirrors SvgRenderer.kt's
    // two-pass label nudging). Runs once per visibleEdges change and
    // produces a per-edge resolved label centre that overlap-aware
    // EdgeLabel components consume via prop. Without this, parallel
    // edges in tree subgraphs (mrtree-laid pools) overlap their labels.
    const resolvedLabelPositions = useMemo(
        () => resolveAllLabelPositions(visibleEdges),
        [visibleEdges],
    )

    // Flush any pending RAF on unmount
    useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

    // Schedule a transform update batched to the next animation frame
    const scheduleTransform = useCallback((updater: (prev: Transform) => Transform) => {
        // Compute eagerly so multiple calls in one frame compose correctly
        setTransform(prev => {
            const next = updater(prev)
            pendingTransformRef.current = next
            return next
        })
        // For very large graphs, throttle re-renders to animation frames
        // (React batches setState in event handlers anyway, but this guards against
        // browsers that fire pointermove faster than rAF)
    }, [setTransform])

    /* Wheel zoom */
    useEffect(() => {
        const svg = svgRef.current
        if (!svg) return
        // Skip wheel zoom on touch-primary devices — they use pinch via pointer events
        const isTouch = window.matchMedia('(pointer: coarse)').matches
        if (isTouch) return
        const onWheel = (e: WheelEvent) => {
            e.preventDefault()
            const rect = svg.getBoundingClientRect()
            const mouseX = e.clientX - rect.left
            const mouseY = e.clientY - rect.top
            // Scale zoom step by deltaY magnitude so Mac trackpad pinch
            // (many small-delta events per gesture) accumulates visibly,
            // while a single mouse-wheel tick (deltaY ~100) still gives a
            // snappy response. Fixed 8%/click felt like "5 pinches to
            // see a change" on laptops. Clamp deltaY so a jittery wheel
            // driver can't trigger a single extreme zoom jump.
            const d = Math.max(-100, Math.min(100, e.deltaY))
            const zoomFactor = Math.exp(-d * 0.01)
            scheduleTransform(prev => {
                const newScale = Math.max(0.1, Math.min(10, prev.scale * zoomFactor))
                const ratio = newScale / prev.scale
                return { x: mouseX - (mouseX - prev.x) * ratio, y: mouseY - (mouseY - prev.y) * ratio, scale: newScale }
            })
        }
        svg.addEventListener('wheel', onWheel, { passive: false })
        return () => svg.removeEventListener('wheel', onWheel)
    }, [scheduleTransform])

    // Track whether the pointer-down event was a true background tap
    // (no movement above threshold) so we can clear selection on
    // pointer-up rather than pointer-down. Clearing on pointer-down
    // dropped selection every time the user STARTED a pan, which made
    // selection-driven UI feel broken.
    const tapRef = useRef<{ pointerId: number; x: number; y: number; moved: boolean } | null>(null)
    const TAP_SLOP_PX = 6

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        if (e.button !== 0) return
        const isNode = (e.target as Element).closest('.gnode')
        if (isNode) return

        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

        // Capture on the SVG element itself, not e.target. e.target may
        // be a child element (region rect, group rect, edge path) which
        // can re-render or unmount during the gesture; capturing on the
        // child means subsequent pointer events route nowhere. Capturing
        // on the SVG keeps the gesture alive for the whole interaction.
        const svg = svgRef.current
        try { svg?.setPointerCapture(e.pointerId) } catch {}

        if (pointersRef.current.size === 1) {
            panRef.current = { active: true, pointerId: e.pointerId, x: e.clientX, y: e.clientY }
            pinchRef.current = null
            // Track for tap-vs-drag classification on pointerup
            tapRef.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, moved: false }
        } else if (pointersRef.current.size === 2) {
            panRef.current = null
            tapRef.current = null
            const [p1, p2] = [...pointersRef.current.entries()]
            const dist = Math.hypot(p2[1].x - p1[1].x, p2[1].y - p1[1].y)
            pinchRef.current = { pointerId1: p1[0], pointerId2: p2[0], dist, midX: (p1[1].x + p2[1].x) / 2, midY: (p1[1].y + p2[1].y) / 2 }
        }
    }, [])

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        // Mark the gesture as a drag if movement crosses the slop
        // threshold. Only true drags suppress the tap-clear-selection
        // on pointerup.
        if (tapRef.current && tapRef.current.pointerId === e.pointerId) {
            const dx = e.clientX - tapRef.current.x, dy = e.clientY - tapRef.current.y
            if (Math.hypot(dx, dy) > TAP_SLOP_PX) tapRef.current.moved = true
        }
        if (pinchRef.current && pointersRef.current.size === 2) {
            const [p1, p2] = [...pointersRef.current.entries()]
            const newDist = Math.hypot(p2[1].x - p1[1].x, p2[1].y - p1[1].y)
            const newMidX = (p1[1].x + p2[1].x) / 2, newMidY = (p1[1].y + p2[1].y) / 2
            const scaleFactor = newDist / pinchRef.current.dist
            const panDx = newMidX - pinchRef.current.midX, panDy = newMidY - pinchRef.current.midY
            const rect = svgRef.current?.getBoundingClientRect()
            const cx = rect ? newMidX - rect.left : newMidX, cy = rect ? newMidY - rect.top : newMidY
            scheduleTransform(prev => {
                const newScale = Math.max(0.1, Math.min(10, prev.scale * scaleFactor))
                const ratio = newScale / prev.scale
                return { x: cx - (cx - prev.x) * ratio + panDx, y: cy - (cy - prev.y) * ratio + panDy, scale: newScale }
            })
            pinchRef.current = { ...pinchRef.current, dist: newDist, midX: newMidX, midY: newMidY }
            return
        }
        if (panRef.current?.active && e.pointerId === panRef.current.pointerId) {
            const dx = e.clientX - panRef.current.x, dy = e.clientY - panRef.current.y
            panRef.current.x = e.clientX; panRef.current.y = e.clientY
            scheduleTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }))
        }
    }, [scheduleTransform])

    /**
     * Shared cleanup for pointerup and pointercancel. Mobile OSes fire
     * pointercancel for system gestures (incoming notifications, scroll
     * intent, long-press menus, etc.) without ever firing pointerup —
     * so without a cancel handler, panRef and pointersRef accumulate
     * stale entries and the canvas locks up. This handles both events
     * with the same logic.
     */
    const releasePointer = useCallback((pointerId: number, treatAsTap: boolean) => {
        pointersRef.current.delete(pointerId)
        if (panRef.current?.pointerId === pointerId) panRef.current = null

        // Tap classification: a true background tap (no drag) clears
        // selection. We do it on release rather than press so panning
        // doesn't drop the user's selection on every gesture start.
        if (treatAsTap && tapRef.current && tapRef.current.pointerId === pointerId && !tapRef.current.moved) {
            onNodeClick?.('', false)
        }
        if (tapRef.current?.pointerId === pointerId) tapRef.current = null

        if (pinchRef.current) {
            pinchRef.current = null
            // Pinch ended via one finger lifting — keep the remaining
            // pointer alive as a pan so the gesture transitions cleanly
            // from two-finger pinch to one-finger pan.
            if (pointersRef.current.size === 1) {
                const [id, pos] = [...pointersRef.current.entries()][0]
                panRef.current = { active: true, pointerId: id, x: pos.x, y: pos.y }
            }
        }
    }, [onNodeClick])

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
        releasePointer(e.pointerId, /* treatAsTap = */ true)
        try { svgRef.current?.releasePointerCapture(e.pointerId) } catch {}
    }, [releasePointer])

    const handlePointerCancel = useCallback((e: React.PointerEvent) => {
        // System cancellation — never treat as tap, never clear selection.
        releasePointer(e.pointerId, /* treatAsTap = */ false)
        try { svgRef.current?.releasePointerCapture(e.pointerId) } catch {}
    }, [releasePointer])

    const handleNodeEnter = useCallback((nodeId: string) => {
        setHoveredNodeId(nodeId); onNodeHover?.(nodeId)
    }, [onNodeHover])

    const handleNodeLeave = useCallback(() => {
        setHoveredNodeId(null); onNodeHover?.(null)
    }, [onNodeHover])

    // Only show particles on small graphs with simulation active
    const showParticles = isSimulating && !isLargeGraph

    return (
        <svg
            ref={svgRef as React.RefObject<SVGSVGElement>}
            className={styles.canvas}
            // Theme-driven font character (Blueprint = Inter, others = mono).
            style={{ fontFamily: scene.metadata.fontFamily ?? 'var(--font-ui)' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerCancel}
        >
            {/* SVG filters are expensive — only define them on small graphs */}
            {!isLargeGraph && (
                <defs>
                    <filter id="glow-bridge" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="4" result="blur" />
                        <feFlood floodColor={theme.roleLocus} floodOpacity="0.35" />
                        <feComposite in2="blur" operator="in" />
                        <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <filter id="particle-glow" x="-200%" y="-200%" width="500%" height="500%">
                        <feGaussianBlur stdDeviation="3" />
                    </filter>
                </defs>
            )}

            <g transform={`translate(${transform.x},${transform.y}) scale(${transform.scale})`}>
                {scene.regions.filter(r => {
                    if (!structureFilters) return true
                    const l = (r.label ?? '').toLowerCase()
                    if (l === 'cycle') return structureFilters.cycles
                    if (l === 'pipeline') return structureFilters.pipelines
                    if (l === 'fork') return structureFilters.forks
                    if (l === 'join') return structureFilters.joins
                    if (l === 'hub') return structureFilters.hubs
                    if (l === 'deadlock') return structureFilters.deadlock
                    if (l === 'overflow') return structureFilters.overflow
                    return true
                }).map((r, i) => <RegionShape key={`region-${i}`} region={r} />)}
                {scene.groups.filter(g => {
                    if (!structureFilters) return true
                    if (g.id.startsWith('component:')) return structureFilters.subsystems
                    return true
                }).map((g, i) => <GroupShape key={`group-${g.id}-${i}`} group={g} />)}
                {scene.locationGroups?.map((lg, i) => <LocationZone key={`loc-${lg.id}-${i}`} group={lg} />)}
                {scene.indicators.map((ind, i) => <IndicatorShape key={`ind-${ind.nodeId}-${i}`} indicator={ind} />)}
                {visibleEdges.map(edge => (
                    <EdgeShape key={edge.id} edge={edge} lightweight={isHugeGraph} />
                ))}
                {showParticles && visibleEdges.map(edge => (
                    <EdgeParticles key={`p-${edge.id}`} edge={edge} speed={simSpeed} />
                ))}
                {visibleEdges.map(edge => (
                    <EdgeMarker key={`m-${edge.id}`} edge={edge} />
                ))}
                {/* Edge labels rendered AFTER nodes (in this same group)
                    so node circles don't cover labels whose midpoint
                    happens to fall inside a node bound. Pre-resolved
                    positions come from a layer-level collision pass
                    that mirrors SvgRenderer.kt's two-pass nudging. */}
                {visibleNodes.map(node => (
                    <NodeCard
                        key={node.id} node={node}
                        isSelected={!!(node.id === selectedNodeId || selectedIds?.has(node.id))}
                        isHovered={node.id === hoveredNodeId}
                        isSimulating={!!isSimulating && !isLargeGraph}
                        onNodeClick={(id, shift) => onNodeClick?.(id, shift)}
                        onNodeDoubleClick={(id, name, old) => onNodeDoubleClick?.(id, name, old)}
                        onNodeEnter={handleNodeEnter}
                        onNodeLeave={handleNodeLeave}
                    />
                ))}
                {/* Arrowheads rendered AFTER nodes so they sit on top
                    of the action circles, not behind them. Without this
                    the node's filled background covers the arrow triangle
                    at the boundary — making it impossible to tell which
                    direction the edge flows. */}
                {visibleEdges.map(edge => (
                    <EdgeArrowHead key={`ah-${edge.id}`} edge={edge} />
                ))}
                {/* Edge labels rendered AFTER nodes + arrowheads */}
                {visibleEdges.map(edge => (
                    <EdgeLabel key={`l-${edge.id}`} edge={edge}
                        resolvedPosition={resolvedLabelPositions.get(edge.id)} />
                ))}
            </g>
        </svg>
    )
}

/* ── Region / Group / Indicator (small, kept inline) ── */

function RegionShape({ region }: { region: SceneRegion }) {
    const x = region.x - region.width / 2, y = region.y - region.height / 2
    return (
        <g>
            {/* Dashed border + tint + label — matches SvgRenderer renderRegionRect */}
            <rect x={x} y={y} width={region.width} height={region.height}
                rx={region.cornerRadius} ry={region.cornerRadius}
                fill={region.fillColor} fillOpacity={region.fillOpacity}
                stroke={region.strokeColor} strokeWidth={region.strokeWidth}
                strokeOpacity={region.strokeOpacity}
                strokeDasharray={region.dashPattern && region.dashPattern.length > 0
                    ? region.dashPattern.join(',')
                    : undefined} />
            {region.label && (
                <text x={x + 8} y={y + 14} fill={region.strokeColor}
                    opacity={0.55} fontSize={8} fontWeight={400}
                    fontFamily="var(--font-mono)"
                    style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {region.label}
                </text>
            )}
        </g>
    )
}

function GroupShape({ group }: { group: SceneGroup }) {
    // Editorial nested-containment: hairline dashed boundary, no fill.
    // fillColor now doubles as the stroke color (set by SceneBuilder to
    // theme.textMuted). Matches SvgRenderer.renderGroup on the server side.
    return (
        <rect x={group.x - group.width / 2} y={group.y - group.height / 2}
            width={group.width} height={group.height} rx={14} ry={14}
            fill="none" stroke={group.fillColor} strokeWidth={1}
            strokeOpacity={0.55} strokeDasharray="4,3" />
    )
}

function LocationZone({ group }: { group: SceneLocationGroup }) {
    const x = group.x - group.width / 2, y = group.y - group.height / 2
    const label = group.label.toLowerCase()
    const tabFontSize = 10
    const tabTextW = label.length * tabFontSize * 0.62
    const tabPadH = 8
    const tabPadV = 4
    const tabW = tabTextW + tabPadH * 2
    const tabH = tabFontSize + tabPadV * 2
    const tabX = x + 8
    const tabY = y + 6

    return (
        <g>
            {/* Zone background — solid border, subtle fill (first-class data) */}
            <rect x={x} y={y} width={group.width} height={group.height}
                rx={10} ry={10}
                fill={group.strokeColor} fillOpacity={0.06}
                stroke={group.strokeColor} strokeWidth={1}
                strokeOpacity={0.25} />
            {/* Header tab — solid pill with location name */}
            <rect x={tabX} y={tabY} width={tabW} height={tabH}
                rx={tabH / 2} ry={tabH / 2}
                fill={group.strokeColor} fillOpacity={0.2} />
            <text x={tabX + tabPadH} y={tabY + tabH / 2}
                fill={group.strokeColor}
                fontSize={tabFontSize} fontWeight={700}
                fontFamily="var(--font-mono)"
                dominantBaseline="central">
                {label}
            </text>
        </g>
    )
}

function IndicatorShape({ indicator }: { indicator: SceneIndicator }) {
    return (
        <rect x={indicator.x - indicator.width / 2} y={indicator.y - indicator.height / 2}
            width={indicator.width} height={indicator.height}
            rx={indicator.cornerRadius} ry={indicator.cornerRadius}
            fill="none" stroke={indicator.strokeColor} strokeWidth={indicator.strokeWidth}
            strokeOpacity={indicator.strokeOpacity * 0.4}
            strokeDasharray={indicator.dashPattern.join(' ')} />
    )
}
